import { useCallback, useId, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import CameraAltRoundedIcon from "@mui/icons-material/CameraAltRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import StepNavigation from "./StepNavigation";
import CameraCapture, { type CameraCaptureHandle } from "./CameraCapture";
import {
  ACCEPTED_MIME,
  MAX_FILE_BYTES,
  base64ByteSize,
  formatBytes,
  type DocumentType,
  type DocumentUpload,
  type UploadedFile,
} from "./types";

const DOCUMENT_OPTIONS: ReadonlyArray<{ value: DocumentType; label: string }> = [
  { value: "passport", label: "Passport" },
  { value: "national_id", label: "National ID card" },
  { value: "driver_license", label: "Driver's license" },
];

const ACCEPT_ATTR = ACCEPTED_MIME.join(",");

const PILL_BUTTON_SX = {
  height: "auto",
  minWidth: "auto",
  borderRadius: "16px",
  border: "1px solid rgba(0, 0, 0, 0.5)",
  color: "common.black",
  fontFamily: "Inter, sans-serif",
  fontWeight: 400,
  fontSize: "12px",
  lineHeight: "21px",
  letterSpacing: 0,
  textTransform: "uppercase" as const,
  bgcolor: "transparent",
  py: "4px",
  px: "10px",
  "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)", border: "1px solid rgba(0, 0, 0, 0.7)" },
};

const readAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });

interface CameraDialogProps {
  open: boolean;
  label: string;
  onCapture: (file: UploadedFile) => void;
  onClose: () => void;
}

const CameraDialog = ({ open, label, onCapture, onClose }: CameraDialogProps): JSX.Element => {
  const cameraRef = useRef<CameraCaptureHandle | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);

  const handleCapture = useCallback(() => {
    const screenshot = cameraRef.current?.getScreenshot();
    if (!screenshot) {
      setCameraError("Could not capture image. Please try again.");
      return;
    }
    setPreview(screenshot);
  }, []);

  const handleRetake = useCallback(() => setPreview(null), []);

  const handleConfirm = useCallback(() => {
    if (!preview) return;
    onCapture({
      name: `document-capture-${Date.now()}.jpg`,
      size: base64ByteSize(preview),
      type: "image/jpeg",
      dataUrl: preview,
    });
    setPreview(null);
    setCameraError(null);
  }, [preview, onCapture]);

  const handleClose = useCallback(() => {
    setPreview(null);
    setCameraError(null);
    onClose();
  }, [onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={{ p: { xs: "18px", md: "24px" }, borderRadius: "20px" }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h6">Capture {label}</Typography>
        <IconButton onClick={handleClose} aria-label="Close camera" size="small">
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {cameraError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCameraError(null)}>
            {cameraError}
          </Alert>
        )}

        <Box
          sx={{
            position: "relative",
            width: "100%",
            aspectRatio: "16 / 10",
            borderRadius: 2,
            overflow: "hidden",
            bgcolor: "grey.900",
          }}
        >
          {preview ? (
            <Box
              component="img"
              src={preview}
              alt="Document preview"
              sx={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
            />
          ) : open ? (
            <CameraCapture
              ref={cameraRef}
              facingMode="environment"
              quality={0.95}
              width={1920}
              height={1080}
              onError={setCameraError}
            />
          ) : null}
        </Box>

        <Typography
          variant="caption"
          color="text.primary"
          sx={{ mt: 1, display: "block", textAlign: "center", fontFamily: "monospace", fontWeight: 700 }}
        >
          {preview ? "Looking good? Confirm or retake." : "Place the document flat, ensure all corners are visible."}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2, justifyContent: "space-between" }}>
        {preview ? (
          <>
            <Button onClick={handleRetake} variant="contained" sx={{ backgroundColor: "black", color: "white" }}>
              Retake
            </Button>
            <Button onClick={handleConfirm} variant="contained" sx={{ backgroundColor: "black", color: "white" }}>
              Use this photo
            </Button>
          </>
        ) : (
          <Button
            onClick={handleCapture}
            startIcon={<CameraAltRoundedIcon />}
            variant="contained"
            fullWidth
            style={{ width: "100%" }}
            disabled={Boolean(cameraError)}
          >
            Capture
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

interface DocumentDropzoneProps {
  title: string;
  description: string;
  value: UploadedFile | null;
  onChange: (file: UploadedFile | null) => void;
}

const DocumentDropzone = ({ title, description, value, onChange }: DocumentDropzoneProps): JSX.Element => {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);

  const handleFile = useCallback(
    async (file: File) => {
      setError(null);
      if (file.size > MAX_FILE_BYTES) {
        setError(`File is too large. Max ${formatBytes(MAX_FILE_BYTES)}.`);
        return;
      }
      if (!ACCEPTED_MIME.includes(file.type)) {
        setError("Unsupported file type.");
        return;
      }
      try {
        const dataUrl = await readAsDataUrl(file);
        onChange({ name: file.name, size: file.size, type: file.type, dataUrl });
      } catch {
        setError("Could not read file. Please try another.");
      }
    },
    [onChange]
  );

  const handleChooseFile = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleClear = useCallback(() => {
    onChange(null);
    if (inputRef.current) inputRef.current.value = "";
  }, [onChange]);

  const handleCameraCapture = useCallback(
    (file: UploadedFile) => {
      onChange(file);
      setCameraOpen(false);
    },
    [onChange]
  );

  const isImage = value?.type.startsWith("image/") ?? false;

  return (
    <Box>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          minHeight: 182,
          borderRadius: "20px",
          border: "2px dashed #3d3d3d",
          bgcolor: "#f2f2f2",
          p: { xs: "18px", md: "24px" },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        {value ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              gap: { xs: 2, md: 3 },
            }}
          >
            <Box
              sx={{
                width: 133,
                height: 133,
                borderRadius: "20px",
                overflow: "hidden",
                bgcolor: "#f5f5f5",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {isImage ? (
                <Box
                  component="img"
                  src={value.dataUrl}
                  alt={value.name}
                  sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              ) : (
                <CloudUploadRoundedIcon sx={{ fontSize: 44, color: "common.black" }} />
              )}
            </Box>

            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontWeight: 700,
                  fontSize: "12px",
                  lineHeight: "16.8px",
                  letterSpacing: "0.084px",
                  textTransform: "uppercase",
                  color: "common.black",
                  wordBreak: "break-word",
                }}
              >
                {value.name}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontWeight: 400,
                  fontSize: "12px",
                  lineHeight: "16.8px",
                  letterSpacing: "0.084px",
                  textTransform: "uppercase",
                  color: "common.black",
                }}
              >
                {`${formatBytes(value.size)} • ${value.type}`}
              </Typography>
            </Box>

            <IconButton
              onClick={handleClear}
              aria-label={`Remove ${title}`}
              sx={{
                width: 32,
                height: 32,
                color: "common.black",
                flexShrink: 0,
                "&:hover": { bgcolor: "rgba(0, 0, 0, 0.06)" },
              }}
            >
              <CloseRoundedIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </Box>
        ) : (
          <>
            <CloudUploadRoundedIcon sx={{ fontSize: 44, color: "common.black" }} />
            <Typography
              sx={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontWeight: 700,
                fontSize: "16px",
                lineHeight: "19.2px",
                letterSpacing: "0.084px",
                textTransform: "uppercase",
                color: "common.black",
                textAlign: "center",
              }}
            >
              {title}
            </Typography>
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 400,
                fontSize: "12px",
                lineHeight: "19.2px",
                textTransform: "uppercase",
                color: "common.black",
                textAlign: "center",
              }}
            >
              {description}
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5, mt: 1, flexWrap: "wrap", justifyContent: "center" }}>
              <Button onClick={handleChooseFile} sx={PILL_BUTTON_SX}>
                Choose file
              </Button>
              <Button
                onClick={() => setCameraOpen(true)}
                startIcon={<CameraAltRoundedIcon sx={{ fontSize: 18 }} />}
                sx={PILL_BUTTON_SX}
              >
                Take a photo
              </Button>
            </Box>
          </>
        )}

        <input
          id={inputId}
          ref={inputRef}
          type="file"
          accept={ACCEPT_ATTR}
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void handleFile(file);
          }}
        />
      </Box>

      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
          {error}
        </Typography>
      )}

      <CameraDialog
        open={cameraOpen}
        label={title.toLowerCase()}
        onCapture={handleCameraCapture}
        onClose={() => setCameraOpen(false)}
      />
    </Box>
  );
};

export interface DocumentUploadStepProps {
  defaultValue: DocumentUpload | null;
  isFirstStep: boolean;
  isLastStep: boolean;
  isSubmitting?: boolean;
  onSubmit: (document: DocumentUpload) => void;
  onBack: () => void;
}

const DocumentUploadStep = ({
  defaultValue,
  isFirstStep,
  isLastStep,
  isSubmitting,
  onSubmit,
  onBack,
}: DocumentUploadStepProps): JSX.Element => {
  const [documentType, setDocumentType] = useState<DocumentType>(defaultValue?.documentType ?? "passport");
  const [frontImage, setFrontImage] = useState<UploadedFile | null>(defaultValue?.frontImage ?? null);
  const [backImage, setBackImage] = useState<UploadedFile | null>(defaultValue?.backImage ?? null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const requiresBack = documentType !== "passport";

  const isValid = useMemo(() => {
    if (!frontImage) return false;
    if (requiresBack && !backImage) return false;
    return true;
  }, [frontImage, backImage, requiresBack]);

  const handleNext = (): void => {
    setSubmitError(null);
    if (!frontImage) {
      setSubmitError("Please upload the front of your document.");
      return;
    }
    if (requiresBack && !backImage) {
      setSubmitError("Back of document is required for this document type.");
      return;
    }
    const payload: DocumentUpload = {
      documentType,
      frontImage,
      ...(backImage ? { backImage } : {}),
    };
    onSubmit(payload);
  };

  return (
    <Box
      sx={{
        bgcolor: "common.white",
        borderRadius: "20px",
        // px: { xs: "18px", md: "24px" },
        // pt: { xs: "18px", md: "24px" },
        // pb: { xs: "18px", md: "24px" },
      }}
    >
      {/* <Typography
        sx={{
          fontFamily: "Inter, sans-serif",
          fontWeight: 400,
          fontSize: "14px",
          lineHeight: "19.2px",
          color: "common.black",
          mb: { xs: 2, md: 3 },
        }}
      >
        Upload your ID
      </Typography> */}

      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
          {submitError}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "24px",
          width: "100%",
          maxWidth: 759,
          mx: "auto",
          p: { xs: "18px", md: "24px" },
        }}
      >
        <TextField
          select
          label="Document type"
          fullWidth
          required
          value={documentType}
          onChange={(event) => {
            const next = event.target.value as DocumentType;
            setDocumentType(next);
            if (next === "passport") setBackImage(null);
          }}
          sx={{
            "& .MuiOutlinedInput-root": { borderRadius: "20px" },
          }}
        >
          {DOCUMENT_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <Box sx={{ width: "100%" }}>
          <DocumentDropzone
            title="Upload or scan front of document"
            description="Drag and drop • JPG, PNG, WEBP or PDF • Max 8.0 MB"
            value={frontImage}
            onChange={setFrontImage}
          />
        </Box>

        {requiresBack && (
          <Box sx={{ width: "100%" }}>
            <DocumentDropzone
              title="Upload or scan back of document"
              description="Drag and drop • JPG, PNG, WEBP or PDF • Max 8.0 MB"
              value={backImage}
              onChange={setBackImage}
            />
          </Box>
        )}
      </Box>

      <StepNavigation
        onBack={onBack}
        onNext={handleNext}
        isFirstStep={isFirstStep}
        isLastStep={isLastStep}
        isSubmitting={isSubmitting}
        nextDisabled={!isValid}
        nextLabel={isLastStep ? "Submit" : undefined}
      />
    </Box>
  );
};

export default DocumentUploadStep;
